import { useBoolean, useControllableValue, useHover } from 'ahooks';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC, Ref, useRef } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { IconShell } from 'components/atoms/icon-shell';
import { InputType } from 'components/atoms/input';
import { confirm, error } from 'components/atoms/modal';
import { ProgressPie } from 'components/atoms/progress-pie';
import {
  MAX_SUBTASKS_ALLOWED,
  MIN_SUBTASKS_WITHOUT_ALERT,
} from 'components/organisms/tasks/constants';
import { ClassNameProps } from 'components/ozone-component';
import { grayTheme } from 'components/styles/theme';
import { logger } from 'services';
import { AddIcon, CloseIcon, Container, Input } from './styles';

interface AddNewRowProps extends ClassNameProps {
  id?: string;
  autoFocus?: boolean;
  clearOnBlur?: boolean;
  size?: SizeType;
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  onAdd: (value: string) => Promise<void>;
  onAddMultiple?: (values: Array<string>) => Promise<void>;
  onBlur?: (value?: string) => void;
  onUpdate?: (value: string) => void;
  inputRef?: Ref<InputType>;
  bulkAddType?: string;
  showStatusIcon?: boolean;
  onClear?: () => void;
  useClearAction?: boolean;
}

const StyledTextArea = styled(Input.TextArea)`
  resize: none;
  padding: var(--spacing-03);
  border: none;
  background: transparent;
  &:focus,
  &:focus-within {
    border: none;
    outline: none;
    box-shadow: none;
  }
`;

export const NewRow: FC<AddNewRowProps> = (props) => {
  const {
    id,
    size = 'middle',
    onAdd,
    placeholder,
    onBlur,
    onUpdate,
    className,
    autoFocus = true,
    clearOnBlur,
    inputRef,
    onAddMultiple,
    bulkAddType,
    showStatusIcon,
    onClear,
    useClearAction,
  } = props;
  const ref = useRef(null);
  const [state, setState] = useControllableValue<string>(props);
  const isHovering = useHover(ref);
  const [isFocused, { setTrue, setFalse }] = useBoolean(false);
  const _bulkAdditionType = bulkAddType || 'items';

  const clearValue = () => {
    setState('');
  };

  const clearAction = () => {
    if (onClear && useClearAction) {
      onClear();
    }
  };

  const handleEnter = async () => {
    const value = state?.trim();

    if (value) {
      try {
        await onAdd(value);
        clearValue();
        clearAction();
      } catch (_error) {
        logger.error('task create failed', { error: _error });
      }
    }
  };

  const handleChange = async ({
    target,
  }: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (target.value.includes('\n')) {
      const subtasks = target.value.split('\n').filter((e) => !!e);

      if (subtasks.length < 2) {
        return;
      }
      if (onAddMultiple) {
        if (subtasks.length < MIN_SUBTASKS_WITHOUT_ALERT) {
          onAddMultiple?.(subtasks);
        } else if (subtasks.length > MAX_SUBTASKS_ALLOWED) {
          error({
            title: `Add ${_bulkAdditionType}!`,
            content: `You cannot add more than 500 ${_bulkAdditionType} at a time!`,
          });
        } else {
          confirm({
            title: `Add ${_bulkAdditionType}`,
            content: `You are creating ${subtasks.length} ${_bulkAdditionType}. Are you sure you want to continue ?`,
            onOk: async () => onAddMultiple?.(subtasks),
          });
        }

        setState('');

        return;
      }
      onAdd(subtasks[0]);
    }
    setState(target.value);

    if (onUpdate) {
      onUpdate(target.value);
    }
  };

  const handleOnBlur = () => {
    setFalse();

    const value = state?.trim();

    if (onBlur) {
      onBlur(value);

      if (clearOnBlur) {
        clearValue();
      }
    }
  };
  const canShowStatusIcon =
    isHovering || isFocused || Boolean(state) || showStatusIcon;

  return (
    <Container ref={ref} size={size} className={className}>
      <IconShell
        size={size}
        icon={
          canShowStatusIcon ? (
            <ProgressPie
              progress={0}
              type={Boolean(state) || isFocused ? 'default' : 'disabled'}
            />
          ) : (
            <AddIcon />
          )
        }
      />
      <ThemeProvider theme={grayTheme}>
        <StyledTextArea
          ref={inputRef}
          id={id}
          autoFocus={autoFocus}
          value={state}
          size={size}
          onFocus={setTrue}
          onBlur={handleOnBlur}
          onPressEnter={handleEnter}
          onChange={handleChange}
          placeholder={placeholder}
          rows={1}
          data-cy="new-row-text-area"
        />
        {useClearAction && onClear ? <CloseIcon onClick={clearAction} /> : null}
      </ThemeProvider>
    </Container>
  );
};
